import {
  clipBoardPayloadDecode,
  clipBoardPayloadEncode,
  removeHideProperties,
  writeClipboard,
} from '../lib/clipboard';
import { Rect } from '../lib/region';
import { hitTestNode } from '../provider/interactive';
import { TYPES } from '../render/constants';
import { Action } from './action';

let memClipboardData = null;
let lastPasteNode = null;

function htmlToText(html) {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = html;
  return tempElement.textContent || tempElement.innerText || '';
}

export async function copyActionHandler(store, nodes) {
  const selectedNodes = nodes || store.api.getSelectedNodes();
  if (selectedNodes.length === 0) return;
  const copyNodes = [...selectedNodes.map(removeHideProperties)];
  const shallowNodes = [];

  copyNodes.forEach((node) => {
    const nodes = store.api.getDescendants(node.id);
    shallowNodes.push(...nodes.map(removeHideProperties));
  });

  const assets = {};

  return copyActionHandlerSync(store, selectedNodes, assets);
}

export function copyActionHandlerSync(store, nodes, assets = {}, shouldWriteClipboard = true) {
  const selectedNodes = nodes;
  if (selectedNodes.length === 0) return;
  const copyNodes = [...selectedNodes.map(removeHideProperties)];
  const shallowNodes = [];

  copyNodes.forEach((node) => {
    const nodes = store.api.getDescendants(node.id);
    shallowNodes.push(...nodes.map(removeHideProperties));
  });

  const payload = {
    nodes: copyNodes,
    shallowNodes: shallowNodes,
    assets: assets,
  };
  const text = clipBoardPayloadEncode(payload);
  if (shouldWriteClipboard) writeClipboard(text);
  memClipboardData = text;
  lastPasteNode = null;
  return text;
}

let textStyleCache = null;
export const copyTextStyleAction = new Action({
  name: 'CopyTextStyle',
  condition: (context) =>
    !context.providers.text.getCurrentSelectionText() &&
    context.api.getSelectedNodes()[0] &&
    context.api.getSelectedNodes()[0].type === TYPES.text,
  handler: async (context, nodes) => {
    nodes = nodes || context.api.getSelectedNodes();
    const text = nodes[0];
    textStyleCache = {
      fill: text.fill,
      stroke: text.stroke,
      strokeWidth: text.strokeWidth,
      background: text.background,
      fontFamily: text.fontFamily,
      effect: text.effect,
      shadow: text.shadow,
    };
  },
  command: 'Ctrl+Alt+C',
  shortcuts: 'Ctrl+Alt+Ç',
});

export const pasteTextStyleAction = new Action({
  name: 'PasteTextStyle',
  condition: (context) =>
    !context.providers.text.getCurrentSelectionText() &&
    textStyleCache &&
    context.api.getSelectedNodes().some((node) => node.type === TYPES.text),
  handler: async (context, nodes) => {
    nodes = nodes || context.api.getSelectedNodes();
    const textNodes = nodes.filter((node) => node.type === TYPES.text);
    if (textStyleCache && textNodes.length > 0) {
      textNodes.forEach((node) => {
        context.api.updateNode(node.id, {
          ...textStyleCache,
        });
      });
    }
  },
  command: 'Ctrl+Alt+V',
  shortcuts: 'Ctrl+Alt+√',
});

export const copyAction = new Action({
  name: 'Copy',
  condition: (context) => !context.providers.text.getCurrentSelectionText(),
  handler: copyActionHandler,
  shortcuts: 'Ctrl+C',
  icon: 'copy',
});

export const duplicateAction = new Action({
  name: 'Duplicate',
  condition: (context) => !context.providers.text.getCurrentSelectionText(),
  handler: async (context, nodes) => {
    await copyAction.handler(context, nodes);
    return pasteAction.handler(context, true);
  },
  shortcuts: 'Ctrl+D',
  icon: 'copy',
});

export const cutAction = new Action({
  name: 'Cut',
  condition: (context) => !context.providers.text.getCurrentSelectionText(),
  handler: async (context, nodes) => {
    nodes = nodes || context.api.getSelectedNodes();
    await copyAction.handler(context, nodes);
    nodes.forEach((node) => {
      context.api.removeNode(node.id);
    });
  },
  shortcuts: 'Ctrl+X',
  icon: 'cut',
});

export function duplicateSync(context, nodes, { keepOriginOffset = true, callback } = {}) {
  const input = copyActionHandlerSync(context, nodes, {}, false);
  const payload = clipBoardPayloadDecode(input);
  return pasteNodesSync(context, payload, { keepOriginOffset, callback });
}

const isiPad = navigator.userAgent.match(/iPad/i) != null;
const isiPhone = navigator.userAgent.match(/iPhone/i) != null;

async function _pasteAction(store, useMemory = false, e) {
  if ((useMemory || isiPad || isiPhone) && memClipboardData) {
    const payload = clipBoardPayloadDecode(memClipboardData);
    if (payload) {
      return pasteNodes(store, payload);
    }
    return;
  }
  await handlePaste(store, e);
}

async function handlePaste(store, e) {
  const interactive = store.providers.interactive;
  const asset = store.providers.asset;
  if (e && e.clipboardData) {
    const items = e.clipboardData.items;
    const list = Array.from(items)
      .map((item) => {
        if (item.type.includes('image')) {
          const blob = item.getAsFile();
          return {
            type: 'image',
            blob,
          };
        } else if (item.type === 'text/plain') {
          const text = e.clipboardData.getData('text/plain');
          // Check for SVG content (handle both standard SVG tags and XML declarations)
          const isSvg =
            (text.includes('<svg') && text.includes('</svg>')) ||
            (text.startsWith('<?xml') && text.includes('<svg') && text.includes('</svg>'));
          if (isSvg) {
            const blob = new Blob([text], { type: 'image/svg+xml' });
            blob.name = 'image.svg';
            return {
              type: 'image',
              blob,
            };
          }
          return {
            type: 'text',
            text: text,
          };
        } else if (item.type === 'text/html') {
          const html = e.clipboardData.getData('text/html');
          return {
            type: 'html',
            html: html,
          };
        } else {
          return null;
        }
      })
      .filter((n) => n);
    const images = list.filter((item) => item.type === 'image');
    const selectedNodes = store.api.getSelectedNodes();
    const firstNode = selectedNodes[0];

    const updateOneImage = async (blob) => {
      const blobUrl = URL.createObjectURL(blob);
      firstNode.src = blobUrl;
      const assetProvider = store.providers.asset;
      const asset = await createAsset(blob, { name: blob.name, asset: assetProvider });
      const isSvg = blob.type === 'image/svg+xml';
      store.api.updateNode(firstNode.id, {
        src: `asset://${asset.id}`,
        meta: {
          imageType: isSvg ? 'svg' : '',
        },
      });
      URL.revokeObjectURL(blobUrl);
      return firstNode;
    };

    if (images.length) {
      const newNodes = [];
      if (images.length === 1 && selectedNodes.length === 1 && firstNode.type === TYPES.image) {
        const item = images[0];
        const blob = item.blob;
        const node = await updateOneImage(blob);
        newNodes.push(node);
      } else {
        const nodes = await Promise.all(
          images.map((item) => {
            const blob = item.blob;
            return createImage(blob, { name: blob.name, store, interactive, asset });
          }),
        );
        newNodes.push(...nodes);
      }
      store.api.selectNode(newNodes.map((n) => n.id));
      return;
    }

    const newNodes = [];
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      if (item.type === 'text') {
        const text = item.text;
        newNodes.push(createText(store, text));
        break;
      } else if (item.type === 'html') {
        const html = item.html;
        const payload = clipBoardPayloadDecode(html);
        if (payload) {
          const isSingleImage =
            payload.nodes.length === 1 &&
            payload.nodes[0].type === TYPES.image &&
            payload.shallowNodes.length === 0;
          const isSingleSelectedImage =
            selectedNodes.length === 1 && selectedNodes[0].type === TYPES.image;
          const isNotSameImage =
            isSingleImage && isSingleSelectedImage
              ? selectedNodes[0].id !== payload.nodes[0].id
              : false;
          if (isSingleSelectedImage && isSingleImage && isNotSameImage) {
            const image = payload.nodes[0];
            const node = selectedNodes[0];
            store.api.updateNode(node.id, {
              src: image.src,
            });
          } else {
            newNodes.push(...(await pasteNodes(store, payload)));
          }
        } else {
          newNodes.push(createText(store, htmlToText(html)));
        }
        break;
      }
    }

    store.api.selectNode(newNodes.map((n) => n.id));
    return;
  }

  try {
    const clipboardContents = await navigator.clipboard.read().catch(async () => {
      // Fallback to reading all data types if Clipboard API fails
      const items = [];
      const types = await navigator.clipboard
        .readText()
        .then((text) => {
          if (text) {
            items.push({
              types: ['text/plain'],
              getType: async () => new Blob([text], { type: 'text/plain' }),
            });
          }
          return navigator.clipboard.read().then((clipboardItems) => {
            clipboardItems.forEach((item) => items.push(item));
            return items;
          });
        })
        .catch(() => []);
      return types;
    });

    for (const item of clipboardContents) {
      if (item.types.includes('image/png')) {
        const blob = await item.getType('image/png');
        const { asset } = store.providers;
        const selectedNodes = store.api.getSelectedNodes();
        const firstNode = selectedNodes[0];
        if (firstNode.type === TYPES.image && selectedNodes.length == 1) {
          const assetItem = await createAsset(blob, { name: blob.name, asset });
          store.api.updateNode(firstNode.id, {
            src: `asset://${assetItem.id}`,
          });
        } else {
          await createImage(blob, { type: 'image/png', store, interactive, asset });
        }
      } else if (item.types.includes('text/html')) {
        const htmlBlob = await item.getType('text/html');
        const text = await htmlBlob.text();
        const payload = clipBoardPayloadDecode(text);
        if (payload) {
          pasteNodes(store, payload);
        } else {
          createText(store, htmlToText(text));
        }
      } else if (item.types.includes('text/plain')) {
        const textBlob = await item.getType('text/plain');
        const text = await textBlob.text();
        createText(store, text);
      }
    }
  } catch (error) {
    console.error(error);
  }
}

async function createAsset(blob, { name, asset: assetProvider }) {
  const src = URL.createObjectURL(blob);
  const asset = await assetProvider.addAsset(src, {
    filename: name || 'clipboard.png',
    source: 'import',
  });
  URL.revokeObjectURL(src);
  return asset;
}

async function createImage(blob, { name, store, interactive, asset }) {
  return new Promise((resolve, reject) => {
    try {
      const src = URL.createObjectURL(blob);
      const img = document.createElement('img');
      const isSvg = blob.type === 'image/svg+xml';
      img.onload = async () => {
        try {
          const node = store.api.addNode({
            x: interactive.x,
            y: interactive.y,
            w: img.width,
            h: img.height,
            type: TYPES.image,
            src: ``,
            meta: {
              imageType: isSvg ? 'svg' : '',
            },
          });
          node.src = src;
          createAsset(blob, { name, asset })
            .then((asset) => {
              store.api.updateNode(node.id, {
                src: `asset://${asset.id}`,
                loading: false,
              });
              URL.revokeObjectURL(src);
            })
            .catch(() => {
              URL.revokeObjectURL(src);
            });
          resolve(node);
        } catch (error) {
          console.error('Error creating image node:', error);
          reject(error);
        }
      };

      img.onerror = (error) => {
        console.error('Error loading image');
        URL.revokeObjectURL(src);
        reject(error);
      };

      img.src = src;
    } catch (error) {
      console.error('Error getting clipboard data:', error);
      reject(error);
    }
  });
}

function createText(stageContext, text) {
  const { interactive } = stageContext.providers;
  const node = stageContext.api.addNode({
    type: TYPES.text,
    text: text,
    fontSize: 48,
    x: interactive.x,
    y: interactive.y,
    w: 400,
    h: 200,
  });
  return node;
}

export const pasteAction = new Action({
  name: 'Paste',
  handler: _pasteAction,
  condition: (context) => !context.providers.text.getCurrentSelectionText(),
  // shortcuts: 'Ctrl+V',
  icon: 'paste',
});

async function importAssets(context, { assets = {}, nodes = [] }) {
  const assetIdsMap = {};
  // add asset
  if (assets) {
    await Promise.all(
      Object.values(assets).map(async (asset) => {
        try {
          await context.providers.asset.getAsset(`asset://${asset.id}`, context);
        } catch {
          const newAsset = await context.providers.asset.addAsset(asset.url, asset.meta);
          assetIdsMap[asset.id] = newAsset.id;
        }
      }),
    );
  }

  Object.values(nodes).map((n) => {
    if (n.src && n.src.startsWith('asset://')) {
      let src = n.src;
      Object.keys(assetIdsMap).map((id) => {
        src = src.replace(id, assetIdsMap[id]);
      });
      n.src = src;
    }
  });
}

export async function pasteNodes(store, payload, keepOriginOffset = false) {
  const selectedNodes = store.api.getSelectedNodes();
  store.api.clearSelect();
  const shallowNodes = payload.shallowNodes || [];
  const copyNodes = payload.nodes;
  const assets = payload.assets || {};
  if (copyNodes.length === 0) return [];
  await importAssets(store, {
    assets,
    nodes: [...copyNodes, ...shallowNodes],
  });
  payload.selectedNodes = selectedNodes;
  const nodes = pasteNodesSync(store, payload, { keepOriginOffset });
  return nodes;
}

export function pasteNodesSync(store, payload, { keepOriginOffset = false, callback } = {}) {
  const newNodes = [];
  const shallowNodes = payload.shallowNodes || [];
  const copyNodes = payload.nodes;
  const selectedNodes = payload.selectedNodes || [];

  if (copyNodes.length === 0) return;
  const firstNode = copyNodes[0];
  const isSinglePage = selectedNodes.length === 1 && selectedNodes[0].type === TYPES.frame;
  const isPageChildren =
    copyNodes
      .map((node) => store.api.getNode(node.parentId))
      .filter((node) => node?.type === TYPES.frame).length > 0;
  let isWrapperSwap = null;

  if (isSinglePage && isPageChildren) {
    const toPage = selectedNodes[0];
    const fromPage = copyNodes.map((node) => store.api.getNode(node.parentId))[0];
    if (toPage && fromPage) {
      if (fromPage.id !== toPage.id) {
        isWrapperSwap = {
          id: toPage.id,
        };
      }
    }
  }

  const { interactive } = store.providers;

  // Smart Duplicate Logic
  let smartDelta = null;
  if (!keepOriginOffset && interactive.smartDuplicateDelta && interactive.lastMovedIds) {
    const sourceIds = copyNodes.map((n) => n.id);
    const isSmartDuplicate =
      sourceIds.length === interactive.lastMovedIds.length &&
      sourceIds.every((id) => interactive.lastMovedIds.includes(id));

    if (isSmartDuplicate) {
      smartDelta = interactive.smartDuplicateDelta;
    }
  }

  if (!lastPasteNode) {
    lastPasteNode = {
      id: firstNode.id,
      count: 1,
      offset: {
        x: interactive.x,
        y: interactive.y,
      },
    };
  } else {
    const hasLastNode = store.api.getNode(lastPasteNode.lastNodeId);
    if (hasLastNode && lastPasteNode.id === firstNode.id) {
      lastPasteNode.count += 1;
    } else {
      lastPasteNode = {
        id: firstNode.id,
        lastNodeId: null,
        count: 1,
        offset: {
          x: interactive.x,
          y: interactive.y,
        },
      };
    }
  }

  function updateShallowNodeParentId(idMap) {
    shallowNodes.forEach((node) => {
      if (idMap[node.parentId]) {
        node.parentId = idMap[node.parentId];
      }
    });
  }

  const gap = 30;

  const x = lastPasteNode.offset?.x ?? interactive.x;
  const y = lastPasteNode.offset?.y ?? interactive.y;

  const rect = Rect.from(firstNode);
  copyNodes.forEach((node) => {
    rect.expandRect(Rect.from(node));
  });

  copyNodes.forEach((node) => {
    const keep = keepOriginOffset;
    let newNodeX, newNodeY;

    if (smartDelta) {
      newNodeX = node.x + smartDelta.x;
      newNodeY = node.y + smartDelta.y;
    } else {
      newNodeX = keep ? node.x : x + node.x - rect.x + gap * lastPasteNode.count;
      newNodeY = keep ? node.y : y + node.y - rect.y + gap * lastPasteNode.count;
    }

    const newNode = {
      ...removeHideProperties(node),
      x: newNodeX,
      y: newNodeY,
    };

    if (isWrapperSwap) {
      newNode.parentId = isWrapperSwap.id;
      newNode.x = node.x;
      newNode.y = node.y;
    } else if (!keepOriginOffset) {
      const parentNode = store.api.getNode(newNode.parentId);
      const targetPage = getCursorTargetPage(store);
      if (targetPage && parentNode && targetPage.id !== parentNode.id) {
        newNode.parentId = targetPage.id;
        newNode.x = node.x + interactive.x - targetPage.x - rect.w / 2;
        newNode.y = node.y + interactive.y - targetPage.y - rect.h;
      } else if (parentNode) {
        newNode.x = Math.max(0, newNode.x - parentNode.x);
        newNode.y = Math.max(0, newNode.y - parentNode.y);
      } else {
        newNode.parentId = '';
      }
    }
    newNodes.push(newNode);
  });

  const idMap = {};

  if (lastPasteNode) {
    lastPasteNode.lastNodeId = newNodes.length ? newNodes[0].id : null;
  }

  newNodes.map((node) => {
    const oldId = node.id;
    const newNode = addNodeWithInput(node, store);

    idMap[oldId] = newNode.id;
    updateShallowNodeParentId(idMap);
  });

  shallowNodes.forEach((node) => {
    const newId = store.api.createId();
    idMap[node.id] = newId;
  });

  updateShallowNodeParentId(idMap);

  shallowNodes.forEach((node) => {
    const newId = idMap[node.id];
    addNodeWithInput({ ...node, id: newId }, store, true);
  });

  store.api.layoutFrame();
  store.api.selectNode(newNodes.map((node) => node.id));

  // Update lastMovedIds to enable chaining of smart duplicate
  if (smartDelta) {
    interactive.lastMovedIds = newNodes.map((n) => n.id);
  }

  callback && callback(idMap);
  return newNodes;
}

function getCursorTargetPage(store) {
  const { interactive } = store.providers;
  const pages = store.api.getFrames();
  let targetPage;
  for (let i = 0; i < pages.length; i++) {
    if (hitTestNode(store, { x: interactive.x, y: interactive.y }, pages[i])) {
      targetPage = pages[i];
      break;
    }
  }
  return targetPage;
}

function addNodeWithInput(node, store, useInputId = false) {
  let newNode;
  if (node.type === TYPES.image) {
    newNode = store.api.addNode(node, { useInputId });
  } else if (node.type === TYPES.text) {
    newNode = store.api.addNode(node, { useInputId });
  } else {
    newNode = store.api.addNode(node, { useInputId });
  }
  return newNode;
}
